/**
 * Audio mix plan: which clips play when, and a piecewise-linear gain envelope for each.
 *
 * The editor applies it with WebAudio gain automation; the render worker applies the same plan
 * sample by sample in numpy. Ducking is computed from where the voice clips are, not with a live
 * compressor, so both sides get exactly the same envelope.
 */

import type { Project } from "./project.gen";
import { clipDuration } from "./timeline";

export interface MixClip {
  clipId: string;
  assetId: string;
  file: string;
  role: "voice" | "music";
  /** timeline start, seconds */
  start: number;
  /** offset into the source file */
  offset: number;
  duration: number;
  /** [time, gain] pairs on the timeline, sorted, linear between points; gain is 0 outside */
  gain: [number, number][];
}

export interface MixPlan {
  duration: number;
  clips: MixClip[];
}

const dbToGain = (db: number) => Math.pow(10, db / 20);

/** Merge voice intervals that overlap or nearly touch (so music doesn't pump between sentences). */
export function voiceRanges(project: Project, bridge: number): [number, number][] {
  const r: [number, number][] = project.audio
    .filter((c) => c.role === "voice")
    .map((c) => [c.start, c.start + clipDuration(project, c)] as [number, number])
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  const out: [number, number][] = [];
  for (const [a, b] of r) {
    const last = out[out.length - 1];
    if (last && a <= last[1] + bridge) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}

/** 0..1 duck depth at t: ramps over `attack` before a voice range and `release` after it. */
export function duckAmount(ranges: [number, number][], attack: number, release: number, t: number): number {
  let w = 0;
  for (const [a, b] of ranges) {
    let v = 0;
    if (t >= a && t <= b) v = 1;
    else if (t < a && t >= a - attack) v = attack > 0 ? 1 - (a - t) / attack : 1;
    else if (t > b && t <= b + release) v = release > 0 ? 1 - (t - b) / release : 1;
    w = Math.max(w, v);
  }
  return w;
}

export function buildMixPlan(project: Project, duration: number): MixPlan {
  const d = project.ducking;
  const ranges = d.enabled ? voiceRanges(project, d.attack + d.release) : [];
  const duckGain = dbToGain(d.amountDb);
  const clips: MixClip[] = [];

  for (const c of project.audio) {
    const asset = project.assets.find((a) => a.id === c.assetId);
    if (!asset) continue;
    const len = clipDuration(project, c);
    if (len <= 0) continue;
    const t0 = c.start;
    const t1 = c.start + len;
    // breakpoints where some factor of the gain changes slope
    const bp = new Set<number>([t0, t1]);
    if (c.fadeIn > 0) bp.add(Math.min(t1, t0 + c.fadeIn));
    if (c.fadeOut > 0) bp.add(Math.max(t0, t1 - c.fadeOut));
    if (c.role === "music") {
      for (const [a, b] of ranges) {
        for (const x of [a - d.attack, a, b, b + d.release]) if (x > t0 && x < t1) bp.add(x);
      }
    }
    const times = [...bp].sort((a, b) => a - b);
    const gain = times.map((t): [number, number] => {
      let g = c.volume;
      if (c.fadeIn > 0) g *= Math.min(1, (t - t0) / c.fadeIn);
      if (c.fadeOut > 0) g *= Math.min(1, (t1 - t) / c.fadeOut);
      if (c.role === "music") g *= 1 - (1 - duckGain) * duckAmount(ranges, d.attack, d.release, t);
      return [round6(t), round6(Math.max(0, g))];
    });
    clips.push({
      clipId: c.id,
      assetId: c.assetId,
      file: asset.file,
      role: c.role,
      start: t0,
      offset: c.trimIn,
      duration: len,
      gain,
    });
  }
  return { duration, clips };
}

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

/** Evaluate a gain envelope at t (used by tests and the WebAudio scheduler). */
export function gainAt(gain: [number, number][], t: number): number {
  if (gain.length === 0 || t < gain[0][0] || t > gain[gain.length - 1][0]) return 0;
  for (let i = 0; i < gain.length - 1; i++) {
    const [ta, ga] = gain[i];
    const [tb, gb] = gain[i + 1];
    if (t >= ta && t <= tb) return tb > ta ? ga + ((gb - ga) * (t - ta)) / (tb - ta) : gb;
  }
  return gain[gain.length - 1][1];
}
